import Marquee from "react-fast-marquee";
import CountUp from "react-countup";
import { useRef } from "react";
import { useInView } from "framer-motion";
import * as Icons from "lucide-react";
import { Reveal, Eyebrow } from "@/lib/motion";
import { AUDIENCES, METHOD } from "@/data/site";

const CountStat = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <span ref={ref} className="font-heading text-4xl font-bold text-brand-light" data-testid="student-count">
      {inView ? <CountUp start={0} end={1200} duration={2.2} separator="," /> : "1,200"}+
    </span>
  );
};

/* ---------------- Trust Strip ---------------- */
export const TrustStrip = () => (
  <section className="relative py-16 border-y border-white/5 bg-ink-soft" data-testid="trust-strip">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <h2 className="text-center font-heading text-2xl md:text-3xl font-medium text-white max-w-2xl mx-auto">
          Built around practical learning, not AI hype.
        </h2>
      </Reveal>
      <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
        <Reveal delay={0.1} className="text-center">
          <p className="font-heading text-xl font-semibold text-white">Suhani Singh</p>
          <p className="text-sm text-slate-400 mt-1">AI &amp; Digital Marketing Trainer</p>
        </Reveal>
        <span className="hidden md:block h-12 w-px bg-white/10" />
        <Reveal delay={0.2} className="text-center">
          <p><CountStat /></p>
          <p className="text-sm text-slate-400 mt-1">Students taught</p>
        </Reveal>
      </div>
    </div>
    <div className="mt-12 opacity-60">
      <Marquee gradient gradientColor="#15151C" gradientWidth={120} speed={40} autoFill>
        {["Generative AI", "Prompt Engineering", "AI Content Creation", "AI Automation", "Digital Marketing", "AI Productivity", "AI Image Generation", "AI for Business"].map((t) => (
          <span key={t} className="mx-8 font-heading text-lg text-slate-500 uppercase tracking-widest">{t}</span>
        ))}
      </Marquee>
    </div>
  </section>
);

/* ---------------- What Is SuhaniEdge ---------------- */
export const WhatIs = () => (
  <section className="relative py-24 md:py-32" data-testid="what-is-section">
    <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
      <Reveal><Eyebrow className="justify-center">What is SuhaniEdge?</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <p className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl leading-snug font-medium text-white">
          SuhaniEdge is an AI education brand founded by <span className="text-brand-light">Suhani Singh</span>, helping learners understand and apply Generative AI, AI tools, content creation, automation and digital growth through practical courses, workshops and coaching.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Big Brand Statement ---------------- */
export const BrandStatement = () => (
  <section className="relative py-28 md:py-40 bg-ink-soft border-y border-white/5 overflow-hidden" data-testid="brand-statement">
    <div className="absolute -top-1/2 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-brand/10 blur-[140px] pointer-events-none" />
    <div className="relative mx-auto max-w-5xl px-6 lg:px-10 text-center">
      <Reveal>
        <p className="font-heading font-semibold tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
          AI is not the future <br className="hidden sm:block" />you wait for.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-4 font-heading font-semibold tracking-tighter text-brand-gradient text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
          It's a skill you build today.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <p className="mt-10 text-lg text-slate-400 max-w-2xl mx-auto">
          SuhaniEdge exists to make modern AI practical, accessible and useful for people who want to learn, create and grow.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Who We Help ---------------- */
export const WhoWeHelp = () => (
  <section className="relative py-24 md:py-32" data-testid="who-we-help">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Who we help</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Practical AI for the people building what's next.</h2>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {AUDIENCES.map((a, i) => {
          const Icon = Icons[a.icon] || Icons.Sparkles;
          return (
            <Reveal key={a.title} delay={i * 0.06}>
              <div data-testid={`audience-card-${a.title.toLowerCase()}`} className="group h-full rounded-3xl bg-ink-surface border border-white/5 p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_0_40px_rgba(139,92,246,0.12)]">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand-light transition-colors group-hover:bg-brand group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-heading text-xl font-semibold text-white">{a.title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{a.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Method ---------------- */
export const Method = () => (
  <section className="relative py-24 md:py-32 bg-ink-soft border-y border-white/5" data-testid="method-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>The SuhaniEdge Learning Method</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Learn → Practice → Build → Apply</h2>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-5 md:grid-cols-4">
        {METHOD.map((m, i) => (
          <Reveal key={m.step} delay={i * 0.1}>
            <div data-testid={`method-step-${m.title.toLowerCase()}`} className="relative h-full rounded-3xl border border-white/5 bg-ink p-8 overflow-hidden">
              <span className="font-heading text-6xl font-bold text-white/5 absolute top-4 right-5">{m.step}</span>
              <p className="text-xs uppercase tracking-[0.25em] text-brand-light font-semibold">Step {m.step}</p>
              <h3 className="mt-4 font-heading text-2xl font-semibold text-white">{m.title}</h3>
              <p className="mt-3 text-slate-400">{m.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
